import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import { Alert, Autocomplete, Button, Grid, Paper, TextField, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { MeansOfEngagementEnum } from '../../Enum/MeansOfEngagementEnum';
import { SummaryNoteTypeEnum } from '../../Enum/SummaryNoteTypeEnum';
import IMessageAlert from '../../Models/Shared/MessageAlert.model';
import { RootState } from '../../reducers';
import { resetAssessmentError } from '../../store/assessment.slice';
import { saveEngagementDraft } from '../../store/documentEngagement.slice';
import { getRequestZCode, saveNewComment } from '../../store/requests.slice';
import { apiString as API_URL } from '../../utils/constants';
import ProcedureSignOff from '../Assessments/ProcedureSignOff/ProcedureSignOff';
import { IOption } from '../interfaces/IOption';
import generateBasicEngagementDraft from './generateBasicEngagementDraft';
import { DocumentEngagement } from '../../Models/DocumentEngagements/DocumentEngagement.model';
import { EngagedProvider } from '../../Models/DocumentEngagements/EngagedProvider.model';
import Authorization from '../../utils/Authorization';

export interface ISignOffWrapperProps {
    meansOfEngagement?: MeansOfEngagementEnum;
    wrapperDraft?: DocumentEngagement;
    memberId?: string;
    requestId?: string;
    enableWarnings?: boolean;
    zcode?: string;
    notes?: string;
    finalizeSignOff?: (notes?: string) => void;
}

export interface ISessionProviderRoleData {
    Id: string;
    RoleName: string;
}

const SignOffWrapper: React.FC<ISignOffWrapperProps> = (props) => {
    const { enableWarnings, wrapperDraft, memberId, requestId, zcode, notes, finalizeSignOff } = props;
    const { selectedRequest } = useSelector((state: RootState) => state.requests);
    const [documentation, setDocumentation] = useState<string>('<p><br></p>');
    const { memberAssessmentHistory } = useSelector((state: RootState) => state.documentEngagement);

    const documentationEditor = useEditor({
        extensions: [StarterKit],
        content: documentation,
        onUpdate: ({ editor }) => {
            setDocumentation(editor.getHTML());
        },
    });

    useEffect(() => {
        if (documentationEditor && documentation !== documentationEditor.getHTML()) {
            documentationEditor.commands.setContent(documentation || '<p><br></p>');
        }
    }, [documentation]);

    const [finish, setFinish] = useState<boolean>(false);
    const [sessionProviderRole, setSessionProviderRole] = useState<ISessionProviderRoleData>();
    const [meansOfEngagementOptions, setMeansOfEngagementOptions] = useState<IOption[]>();
    const [meansOfEngagementValue, setMeansOfEngagementValue] = useState<IOption>();
    const [meansOfEngagementInput, setMeansOfEngagementInput] = useState<string>();
    const [reasonForEngagementOptions, setreasonForEngagementOptions] = useState<IOption[]>();
    const [reasonForEngagementValue, setReasonForEngagementValue] = useState<IOption>();
    const [reasonForEngagementInput, setReasonForEngagementInput] = useState<string>();
    const [wrapperError, setWrapperError] = useState<IMessageAlert>({
        Show: false,
        Message: '',
    });

    const dispatch = useAppDispatch();
    const auth = new Authorization();
    const { error } = useSelector((state: RootState) => state.assessment);
    const handleSaveClick = async () => {
        let usingZcode: string = zcode;
        if (!usingZcode) {
            usingZcode = await getRequestZCode(requestId);
        }
        const draft = generateBasicEngagementDraft({
            meansOfEngagementValue: meansOfEngagementValue?.value,
            reasonForEngagementOption: reasonForEngagementValue,
            memberId,
            notes: notes ?? documentation,
            zcode: usingZcode,
            summaryNoteType: SummaryNoteTypeEnum.Request,
            assessedRoleId: sessionProviderRole?.Id,
            assessedRoleName: sessionProviderRole?.RoleName,
        });
        const engagedProvider = new EngagedProvider();
        engagedProvider.Id = sessionProviderRole?.Id;
        engagedProvider.Name = auth.UserName;
        engagedProvider.Role = sessionProviderRole?.RoleName;
        draft.EngagedProvider = engagedProvider;
        if (selectedRequest) {
            draft.SummaryNotes[0].ReferenceIds = [selectedRequest.Id];
            dispatch(saveNewComment(draft.Notes, selectedRequest.Id));
        }
        const updatedMemberAssessmentHistory = { ...memberAssessmentHistory, EngagementDraft: wrapperDraft ?? draft };
        // Procedure sign-off builds encounter ids for whichever draft is current.
        // Wait until this engagement is saved so those ids are not written onto an assessment that is already open.
        const saved = await dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        if (saved) {
            setFinish(true);
        }
    };

    useEffect(() => {
        // We're going to copy the assessment error and clear it so we don't have to worry about the assessment slice after this point.
        if (error?.Show) {
            setWrapperError({ ...error });
            dispatch(resetAssessmentError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        setFinish(false);
        const sessionProviderRequest = axios.get(`${API_URL}/provider/GetSessionProvider`);
        sessionProviderRequest.then((response) => {
            if (response.data) {
                setSessionProviderRole({
                    Id: response.data?.ProviderId,
                    RoleName: response.data?.Role,
                });
            }
        });
        const reasonForEngagementRequest = axios.get(`${API_URL}/documentengagement/getreasonforvisit`);
        reasonForEngagementRequest.then((response) => {
            if (response.data) {
                const mappedOptions = response.data.map((x) => ({
                    label: x.Name,
                    value: x.Id,
                }));
                const firstOption = mappedOptions.find((option) => option.label === 'Community Resource') ?? mappedOptions[0];
                setReasonForEngagementValue(firstOption);
                setReasonForEngagementInput(firstOption.label);
                setreasonForEngagementOptions(mappedOptions);
            }
        });

        const meansOfEngagementRequest = axios.get(`${API_URL}/documentengagement/getmeansofengagement`);
        meansOfEngagementRequest.then((response) => {
            if (response.data) {
                const mappedOptions = Object.values(response.data).map((label, index) => ({
                    label: label as string,
                    value: (index + 1).toString(),
                }));
                const firstOption = mappedOptions[0];
                setMeansOfEngagementValue(firstOption);
                setMeansOfEngagementInput(firstOption.label);
                setMeansOfEngagementOptions(mappedOptions);
            }
        });
    }, []);

    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);

    const documentationFields = (
        <>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                {reasonForEngagementOptions && (
                    <Autocomplete
                        fullWidth
                        multiple={false}
                        disableClearable
                        getOptionLabel={(option: any) => option.label}
                        id="reason-of-engagement-autocomplete"
                        options={reasonForEngagementOptions}
                        isOptionEqualToValue={(option, value: any) => {
                            return option.value === value.value;
                        }}
                        renderInput={(params) => <TextField {...params} label="Reason for Engagement" />}
                        value={reasonForEngagementValue}
                        inputValue={reasonForEngagementInput}
                        onInputChange={(event, newInputValue) => {
                            setReasonForEngagementInput(newInputValue);
                        }}
                        onChange={(event: any, newValue: any) => {
                            if (newValue?.value) {
                                setReasonForEngagementValue(newValue);
                            } else {
                                setReasonForEngagementValue(newValue);
                            }
                        }}
                    />
                )}
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                {meansOfEngagementOptions && (
                    <Autocomplete
                        fullWidth
                        multiple={false}
                        disableClearable
                        getOptionLabel={(option: any) => option.label}
                        id="means-of-engagement-autocomplete"
                        options={meansOfEngagementOptions}
                        isOptionEqualToValue={(option, value: any) => {
                            return option.value === value.value;
                        }}
                        renderInput={(params) => <TextField {...params} label="Means of Engagement" />}
                        value={meansOfEngagementValue}
                        inputValue={meansOfEngagementInput}
                        onInputChange={(event, newInputValue) => {
                            setMeansOfEngagementInput(newInputValue);
                        }}
                        onChange={(event: any, newValue: any) => {
                            if (newValue?.value) {
                                setMeansOfEngagementValue(newValue);
                            } else {
                                setMeansOfEngagementValue(newValue);
                            }
                        }}
                    />
                )}
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} hidden={notes !== undefined}>
                <Typography align="center" style={{ width: '100%', alignItems: 'center' }} variant="h5">
                    Documentation
                </Typography>
                <RichTextEditor editor={documentationEditor}>
                    <RichTextEditor.Toolbar>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Bold />
                            <RichTextEditor.Italic />
                            <RichTextEditor.Underline />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.BulletList />
                            <RichTextEditor.OrderedList />
                            <RichTextEditor.H1 />
                            <RichTextEditor.H2 />
                            <RichTextEditor.H3 />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Superscript />
                            <RichTextEditor.Subscript />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.AlignLeft />
                            <RichTextEditor.AlignCenter />
                            <RichTextEditor.AlignRight />
                        </RichTextEditor.ControlsGroup>
                    </RichTextEditor.Toolbar>
                    <RichTextEditor.Content />
                </RichTextEditor>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Tooltip title="Be sure you're finished before moving on, you can't come back to the notes on this screen after saving the draft.">
                    <Button
                        color="success"
                        startIcon={<HistoryEduIcon />}
                        variant="contained"
                        disabled={
                            !(
                                sessionProviderRole?.Id &&
                                reasonForEngagementValue?.label &&
                                reasonForEngagementValue?.value &&
                                meansOfEngagementValue?.value &&
                                documentation !== '<p><br></p>'
                            )
                        }
                        onClick={() => {
                            handleSaveClick();
                        }}
                    >
                        Draft Engagement and Sign Off
                    </Button>
                </Tooltip>
            </Grid>
        </>
    );

    const engagementInfoPage = (
        <Paper variant="outlined" style={{ padding: 10 }}>
            <Grid container direction={'row'} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} hidden={!wrapperError?.Show}>
                    <Alert onClose={() => setWrapperError({ Show: false, Message: '' })} severity="error">
                        {wrapperError?.Message}
                    </Alert>
                </Grid>
                {!finish && documentationFields}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    {finish && memberId && engagementDraft?.MemberId?.length > 0 && engagementDraft?.Id?.length > 0 && (
                        <ProcedureSignOff
                            noAppointments={true}
                            memberId={memberId}
                            requestId={requestId}
                            disableWarnings={!enableWarnings}
                            notAssessment={true}
                            finalizeSignOff={finalizeSignOff}
                            isNotEncounterableOnly={true}
                        />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );

    return engagementInfoPage;
};

export default SignOffWrapper;
